import {
    Async,
    CommandBarButton,
    DirectionalHint,
    FocusZone,
    FocusZoneDirection,
    FocusZoneTabbableElements,
    IButton,
    Icon,
    IconButton,
    MessageBarType,
    TextField,
    TooltipHost
} from "@fluentui/react";
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider,
    Button
} from "@fluentui/react-components";
import { observer } from "mobx-react";
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import * as React from "react";
import { TAGNAME_CHAR_LIMIT, TAGNAME_ERROR_TIMEOUT, TEAM_PICKER_ICON_ID } from "sh-application/../StaffHubConstants";
import setGlobalMessages from "sh-application/actions/setGlobalMessages";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import {
    ScheduleGridUtils,
    setIsReorderTagsPanelOpen,
    setMemberPanelValues,
    toggleTagView,
    updateFilteredTags
} from "sh-application/components/schedules/lib";
import { StaffHubTourMarker } from "sh-application/components/tour/sh-tour/StaffHubTourMarker";
import { appViewState, setHoverColumnHidden } from "sh-application/store";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import DateUtils from "sh-application/utility/DateUtils";
import InstrumentationUtils from "sh-application/utility/InstrumentationUtils";
import KeyboardUtils from "sh-application/utility/KeyboardUtils";
import TagUtils from "sh-application/utility/TagUtils";
import showConfirmDialog from "sh-confirm-dialog/lib/components/ConfirmDialog";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { ITagEntity } from "sh-models";
import {
    ECSConfigKey,
    ECSConfigService,
    InstrumentationService,
    StaffHubErrorCodes,
    TagDataService
} from "sh-services";
import StringsStore from "sh-strings/store";
import { TagStore } from "sh-tag-store";
import { TeamStore } from "sh-team-store";
import { UserStore } from "sh-stores";

import { StaffHubHttpError } from "../../..";
import SpacerItem from "../../commandbar/SpacerItem";
import { PeopleCount } from "../../common/peopleCount/PeopleCount";
import { setMemberDialogOpen, setMemberOpenDialogTriggerButton } from "../../memberEditor/lib";
import schedulesViewStateStore from "../../schedules/lib/store/store";

import { toggleMuteScheduleGroup, UserTeamSettingsStore } from "sh-stores/sh-userteamsettings-store";

import { getDeleteGroupMenuProps, getPeopleCountProps, getRenameGroupMenuProps } from "./TagHeader.mappers";
import { TagHeaderProps } from "./TagHeaderProps";
import setTagName from "./lib/mutators/setTagName";
import setTagNameError from "./lib/mutators/setTagNameError";
import { TagHeaderViewStateStore } from "./lib/store/schema/TagHeaderViewStateStoreSchema";
import createtagHeaderViewStateStore from "./lib/store/store";
import MuteScheduleGroupCoachmark from "./MuteScheduleGroupCoachmark";

const styles = require("./TagHeader.scss");
const classNames = require("classnames/bind");
const cssExports = require("styles/Exports.scss");

/**
 * Calculate the element id for a tag group header element.
 * This is used for Aria accessibility label references
 * @param tagId
 */
export function calculateTagHeaderElementId(tagId: string): string {
    return tagId ? `sh-th-${tagId}` : "sh-th-none";
}

interface TagHeaderState {
    // True once the more menu button DOM element has been located.
    // Stored in state (not just a ref mutation) so that setting it triggers a re-render,
    // which allows the MuteScheduleGroupCoachmark child to see targetRef.current as non-null.
    moreMenuButtonReady: boolean;
}

/**
 * The header component for groups in Schedule page.
 */
@observer
export default class TagHeader extends React.Component<TagHeaderProps, TagHeaderState> {
    private _strings: Map<string, string>;
    private _commonStrings: Map<string, string>;
    private _teamSettingsStrings: Map<string, string>;
    private _tagHeaderViewState: TagHeaderViewStateStore;
    private _addMember = React.createRef<IButton>();
    private _moreMenuButton = React.createRef<IButton>();
    private _moreMenuButtonElement = React.createRef<HTMLElement>();
    private _async = new Async(this);

    constructor(props: TagHeaderProps) {
        super(props);
        /* Import string modules */
        this._strings = StringsStore().registeredStringModules.get("schedulePage").strings;
        this._commonStrings = StringsStore().registeredStringModules.get("common").strings;
        this._teamSettingsStrings =  StringsStore().registeredStringModules.get("teamsettings").strings;
        this._tagHeaderViewState = createtagHeaderViewStateStore(this.getTagId());

        // Initialize state
        this.state = { moreMenuButtonReady: false };

        // this._tagHeaderViewState is used to back the TextField component with an observable value for the tag group
        // name.
        // Here we initialize the viewstate with the current tag group name value from props.
        if (props.tag) {
            setTagName(this._tagHeaderViewState, props.tag.name);
        }
    }

    /**
     * Set up reference to the actual DOM element for the more menu button after mount
     */
    componentDidMount() {
        this.updateMoreMenuButtonElement();
    }

    /**
     * Update reference to the more menu button DOM element after updates
     */
    componentDidUpdate() {
        this.updateMoreMenuButtonElement();
    }

    /**
     * Find and store the actual DOM element for the more menu button.
     * Calls setState when found so React re-renders this component, allowing
     * MuteScheduleGroupCoachmark to see the updated targetRef.current.
     */
    private updateMoreMenuButtonElement() {
        if (!this._moreMenuButtonElement.current) {
            // Query for the button within this tag header's container
            const tagHeaderId = calculateTagHeaderElementId(this.getTagId());
            const tagHeaderElement = document.getElementById(tagHeaderId);
            if (tagHeaderElement) {
                const automationId = AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader");
                const buttonElement = tagHeaderElement.querySelector(`[data-automation-id="${automationId}"]`) as HTMLElement;
                if (buttonElement) {
                    (this._moreMenuButtonElement as any).current = buttonElement;
                    // Trigger re-render so MuteScheduleGroupCoachmark sees the updated ref.
                    // Ref mutations alone don't cause re-renders.
                    this.setState({ moreMenuButtonReady: true });
                }
            }
        }
    }

    /**
     * Cleans up timer when component is unmounted.
     */
     componentWillUnmount() {
        // clear all intervals
        this._async.dispose();
    }

    /**
     * UNSAFE_componentWillReceiveProps will run any time the service updates that tag name.
     * This is to ensure there isn't a cached version of edits that could reset the tag name.
     * @param nextProps
     */
    UNSAFE_componentWillReceiveProps(nextProps: TagHeaderProps) {
        if (nextProps.tag) {
            setTagName(this._tagHeaderViewState, nextProps.tag.name);
        }
    }

    /**
     * Return the tag id
     */
    private getTagId(): string {
        return this.props.tag ? this.props.tag.id : this.props.otherGroupTagId;
    }

    /**
     * Return the display name for the tag
     */
    private getTagName(): string {
        return this._tagHeaderViewState.tagName || this.props.otherGroupTagName;
    }

    /**
     * Return the Aria accessibility label text for the tag header
     */
    private getTagHeaderAriaLabel(): string {
        const tagName: string = this.getTagName();
        return tagName ? tagName : this._strings.get("emptyTagGroupNameAria");
    }

    /**
     * Renders the group name and hours for the new schedule UI.
     */
    private renderFREUIForGroupNameAndHours() {
        const { editEnabled, tag } = this.props;
        const peopleCountProps = getPeopleCountProps(tag, this.props.groupData);

        return <>
            {this.renderCollapseButton()}
            {this.renderGroupNameFREUI()}
            {this.renderGroupHours()}
            {peopleCountProps ? <PeopleCount {...peopleCountProps} /> : null}
            {
                editEnabled ?
                <>
                    {SpacerItem.getComponent(true, undefined, true)}
                    {!this.props.hideActions && this.renderMoreIcon()}
                </> : null
            }
        </>;
    }

    /**
     * Renders the group name as a label for the new schedule UI.
     */
    private renderGroupNameFREUI() {
        return <div data-automation-id={ AutomationUtils.getAutomationId("group", "QAIDGroupName") } className={ styles.tagNameFRE }>
            { this.getTagName() || this._commonStrings.get("unnamedGroup") }
        </div>;
    }

    /**
     * Renders the group name text field for editing the group name
     */
    private renderGroupNameTextField() {
        const viewState = this.props;
        const tabIndex = viewState.isNotFocusable ? -1 : 0;
        return (
            <>
                <TextField
                    autoFocus={ this.autoFocusOnTag() }
                    className={ AutomationUtils.getAutomationId("group", "QAIDGroupNameTextField") }
                    tabIndex={ tabIndex }
                    placeholder={ viewState.editEnabled ? this._strings.get("enterGroupName") : "" }
                    ariaLabel={ this._strings.get("groupNameFieldAriaLabel") }
                    borderless={ true }
                    defaultValue={ this.getTagName() }
                    disabled={ viewState.tag === null || !viewState.editEnabled || viewState.isNotFocusable }  // The default tag's name cannot be edited.
                    value={ undefined }
                    maxLength={ TAGNAME_CHAR_LIMIT }
                    onChange={ this.onTagNameTextFieldChanged }
                    onKeyPress={ this.onTagNameTextFieldKeyPress } />
                { ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableShiftsTargetingHelperText) && <div className={ styles.renameGroupHelperText }>{ this._strings.get("renameGroupHelperText") }</div> }
            </>
        );
    }

    /**
     * Renders the expand/collapse button of the group
     */
    private renderCollapseButton() {
        const viewState = this.props;
        const collapseButtonAriaLabel = this._strings.get("collapseTagAriaLabelFRE");
        const expandButtonAriaLabel = this._strings.get("expandTagAriaLabelFRE");
        const collapseGroupAriaLabel = viewState.isTagCollapsed
            ? expandButtonAriaLabel.format((this.getTagName() || this._commonStrings.get("unnamedGroup")))
            : collapseButtonAriaLabel.format((this.getTagName() || this._commonStrings.get("unnamedGroup")));

        const iconExpanded = "teams-triangle-down";
        const iconCollapsed = "teams-triangle-right";
        return !viewState.hideCollapseButton &&
            <Icon
                ariaLabel={ collapseGroupAriaLabel }
                tabIndex={ 0 }
                data-is-focusable='true'
                data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDCollapseIcon") }
                className={ classNames(styles.collapseIconFRE, "no-print") }
                iconName={ viewState.isTagCollapsed ? iconCollapsed : iconExpanded }
                aria-pressed={ viewState.isTagCollapsed }
                role={ "button" }
                onClick={ this.onTagCollapsed }
                onKeyDown={ this.onTagCollapseKeyDown } />;
    }

    /**
     * Renders the total working hours of the group
     */
    private renderGroupHours() {
        const viewState = this.props;
        const hoursString = this._commonStrings.get("hoursShortFormat").format(DateUtils.getHoursRoundedForDisplay(viewState.hours));
        const groupTotalStyle = styles.groupTotalFRE;
        return <div className={ groupTotalStyle }>
            {
                    !viewState.hideGroupTotal &&
                        <span className={ classNames(styles.groupTotalText) }>
                            { hoursString }
                        </span>
                }
            </div>;
    }

    render() {
        const viewState = this.props;
        const tagHeaderElementId = calculateTagHeaderElementId(this.getTagId());
        const ariaProps: AriaProperties = {
            role: AriaRoles.rowheader,
            label: this.getTagHeaderAriaLabel()
        };

        // Extract fresShownSoFar at component level so MobX can track changes (same pattern as AutoScheduleButton)
        const fresShownSoFar = UserStore().userSettings?.fresShownSoFar || [];

        return <>
            <FocusZone
                isCircularNavigation={false}
                handleTabKey={FocusZoneTabbableElements.all}
                direction={FocusZoneDirection.horizontal}
                id={ tagHeaderElementId }
                className={ styles.tagGroupHeader }
                {...generateDomPropertiesForAria(ariaProps) }>
                {
                    this.renderFREUIForGroupNameAndHours()
                }
                {
                    // Do not show any options for default tag
                    viewState.tag && viewState.editEnabled && !this.props.hideActions &&
                        this.renderGroupSettingsWithNewFRE()
                }
            </FocusZone>
            {/* Render coachmark for the first schedule group (only when feature is enabled) */}
            {TagUtils.shouldShowMuteScheduleGroup(viewState.tag, TeamStore().teamId, viewState.editEnabled) && (
                <MuteScheduleGroupCoachmark
                    targetRef={this._moreMenuButtonElement}
                    isFirstGroup={viewState.isFirstGroup ?? false}
                    tag={viewState.tag}
                    fresShownSoFar={fresShownSoFar}
                />
            )}
        </>;
    }

    /**
     * Returns true if the focusedTagId in the TagStore is the tagId of this TagHeader
     */
    private autoFocusOnTag = () => {
        return this.props.tag && (TagStore().focusedTagId === this.props.tag.id);
    }

    /**
     * Change the local value that tracks the name of the tag
     */
    private onTagNameTextFieldChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setTagName(this._tagHeaderViewState, newValue);
    }

    /**
     *  Callback fired on text field key press to shift focus to add member.
     */

    private onTagNameTextFieldKeyPress = (key: React.KeyboardEvent<HTMLElement>) => {
        if (KeyboardUtils.isEnterPressed(key)) {
            key.stopPropagation();
            this.saveCurrentTagName();
            this._async.setTimeout(() => {
                if (this._addMember && this._addMember.current) {
                    this._addMember.current.focus();
                }
            }, 0);
        }
    }

    private clearTagNameError = () => {
        setTagNameError(this._tagHeaderViewState, "");
    }

    /**
     * Raise rename tag action with the current value from the text field
     */
    private async saveCurrentTagName() {
        // allow empty string but not null or undefined for this._tagHeaderViewState.tagName.  Don't save if no changes
        if (this.props.tag && (this._tagHeaderViewState.tagName != null) && (this._tagHeaderViewState.tagName !== this.props.tag.name)) {
            const updatedTag = TagUtils.createCloneWithNewTagName(this.props.tag.id, this._tagHeaderViewState.tagName);
            if (updatedTag) {
                try {
                    // save the tag
                    let savedTag = await TagDataService.saveTag(updatedTag, false /* isNewTag */, true /* optimistic */, false /* setIsFocused */, false /* updateCacheOnly */, true /* throwError */);
                    if (savedTag) {
                        // when the tagHeaderView state is updated with new value, scheduleHeaderViewStore filteredTags data needs to be updated with new values as well.
                        updateFilteredTags(savedTag);
                        fireAccessibilityAlert(this._strings.get("groupNameSaved"));
                    }
                } catch (error) {
                    const httpError = error as StaffHubHttpError;
                    if (httpError && httpError.staffHubInnerErrorCode && httpError.staffHubInnerErrorCode === StaffHubErrorCodes.TagNameTaken) {
                        // Error when same name exists
                        setTagNameError(this._tagHeaderViewState, this._strings.get("groupNameExistsError"));
                        fireAccessibilityAlert(this._strings.get("groupNameExistsError"));
                    } else {
                        // Any generic error
                        setTagNameError(this._tagHeaderViewState, this._strings.get("groupNameError"));
                        fireAccessibilityAlert(this._strings.get("groupNameError"));
                    }

                    // Clear the time out
                    this._async.setTimeout(() => {
                        this.clearTagNameError();
                    }, TAGNAME_ERROR_TIMEOUT);
                }
            }
        }
    }

    /**
     * Show or hide the hover column
     */
    private hideHoverColumn(visible: boolean) {
        setHoverColumnHidden(schedulesViewStateStore(), visible);
    }

    /**
     * Render the action icons for the group with new FRE changes
     */
    private renderGroupSettingsWithNewFRE() {
        const viewState = this.props;
        const tabIndex = viewState.isNotFocusable ? -1 : 0;
        const addMemberDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? this._strings.get("addMemberDisableButtonInYourShifts") : this._strings.get("addMemberDisableButtonInFilter");

        const newAddMembersViewEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NewAddMembersViewEnabled);
        const text = newAddMembersViewEnabled ? this._strings.get("addMembersTitle") : this._strings.get("addPeopleTitle");
        return (
                <div className={ styles.groupSettings }>
                    {
                        // Add member option is shown only for active tags and not in shift overview.
                        TagUtils.isActiveTag(viewState.tag) &&
                            <TooltipHost
                                content={ viewState.disableAddMember ? addMemberDisableString : this._strings.get("addMemberLabel") }
                                hostClassName={ styles.openShiftsInfoTooltipHost }
                                directionalHint={ DirectionalHint.topCenter }
                                calloutProps={ { isBeakVisible: false,
                                gapSpace: parseInt(cssExports.tooltipGapSpace),
                                // ariaLabel doesn't work here as the focus doesn't go on the tooltip. Add aria label for the icon instead
                                className: styles.openShiftsInfoCallout } }
                                styles={ {
                                    root: styles.toolTipRoot
                                } }>
                                    <CommandBarButton tabIndex={ tabIndex }
                                        text={ text }
                                        checked={ false }
                                        allowDisabledFocus={ true }
                                        disabled={ viewState.disableAddMember }
                                        componentRef={ this._addMember }
                                        className={ classNames(styles.settingIcon, styles.transparentButton, styles.notRound) }
                                        ariaLabel={ viewState.disableAddMember ? addMemberDisableString : this._strings.get("addTeamMemberToTag") }
                                        onClick={ this.onAddMemberToTagClicked }
                                        iconProps={ { className: classNames(StaffHubTourMarker.addMember ,  { [styles.disableIcon]: viewState.disableAddMember }, "no-print"), iconName: "teams-add-member" } }
                                        styles={ {icon: styles.buttonIcon, root: styles.buttonRoot} }
                                        data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDAddMemberToGroup") } />
                            </TooltipHost>
                    }
                </div>
        );
    }

    /**
     * Render the more icon for rename, reorder, delete group options
     */
    private renderMoreIcon() {
        const viewState = this.props;
        const reorderMemberDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? this._strings.get("reorderMemberDisableButtonInYourShifts") : this._strings.get("reorderMemberDisableButtonInFilter");
        const deleteGroupDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? this._strings.get("deleteGroupDisableButtonInYourShifts") : this._strings.get("deleteGroupDisableButtonInFilter");
        const reorderGroupLabel = viewState.disableReorderGroup ? reorderMemberDisableString : this._strings.get("reorderTags");
        const deleteGroupLabel = viewState.disableDeleteGroup ? deleteGroupDisableString : this._strings.get("deleteTag");
        const renameMenuItemProps = getRenameGroupMenuProps();
        const deleteMenuItemProps = getDeleteGroupMenuProps(this.props);

        /*
         * MUTE SCHEDULE GROUP / UNMUTE SCHEDULE GROUP FEATURE
         *
         * WHY: Users want to control which schedule groups they receive notifications for.
         * By default, users receive notifications for all schedule groups and shift requests
         * (swap, time off, offers, open shift requests). This feature lets them selectively mute specific schedule groups.
         *
         * HOW IT WORKS:
         * - Each schedule group has a "Mute group" or "Unmute group" option in the "..." more menu
         * - Unmuted (default): User receives notifications for all requests in this schedule group
         * - Muted: User stops receiving notifications for requests in this schedule group
         * - Mute schedule group status is stored in UserTeamSettings.mutedScheduleGroupIds
         *
         * UI STATES:
         * - Menu item label changes: "Mute group" <-> "Unmute group"
         * - Icon changes: bell (unmuted) <-> mute (muted)
         */
        const isMuteFeatureEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableMuteScheduleGroups);
        const showMuteMenuItem = isMuteFeatureEnabled && TagUtils.isActiveTag(viewState.tag) && TeamStore().teamId;

        let muteLabel: string;
        let muteIconName: string;
        let isMuteMenuItemLoading = false;

        if (showMuteMenuItem) {
            // Get current mute state from store
            const teamId = TeamStore().teamId;
            const scheduleGroupId = viewState.tag.id;
            const muteStatusMap = UserTeamSettingsStore().muteScheduleGroupStatusByTeamAndGroup.get(teamId);
            // Default to unmuted: Users have all groups unmuted by default until they explicitly mute
            const muted = muteStatusMap?.get(scheduleGroupId) ?? false;

            // Check if toggle operation is in progress (for accessibility: disabled state)
            const loadingMap = UserTeamSettingsStore().toggleLoadingByTeamAndGroup.get(teamId);
            isMuteMenuItemLoading = loadingMap?.get(scheduleGroupId) ?? false;

            // Determine label and icon based on mute state
            muteLabel = !muted ? this._strings.get("muteScheduleGroupMenuItem") : this._strings.get("unmuteScheduleGroupMenuItem");
            muteIconName = !muted ? "alert-off-20X20" : "alert-on-20X20";
        }

        // Use Fluent UI v9 Menu when mute feature is enabled
        if (isMuteFeatureEnabled) {
            return (
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button
                            appearance="subtle"
                            icon={<Icon iconName="teams-more-svg" />}
                            aria-label={this._strings.get("moreOptionsLabel")}
                            title={this._strings.get("moreOptionsLabel")}
                            data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader")}
                            className={classNames(styles.settingIcon, styles.transparentButton, styles.moreIcon)}
                        />
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {showMuteMenuItem && (
                                <MenuItem
                                    key="mute-group"
                                    icon={<Icon iconName={muteIconName} />}
                                    onClick={this.onMuteScheduleGroupClicked}
                                    disabled={isMuteMenuItemLoading}
                                    data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMuteScheduleGroupMenuOption")}
                                >
                                    {muteLabel}
                                </MenuItem>
                            )}
                            <MenuItem
                                key="rename-group"
                                icon={<Icon iconName="teams-edit" />}
                                onClick={this.onRenameGroupClicked}
                                disabled={renameMenuItemProps?.disabled}
                                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDRenameGroupMenuOption")}
                            >
                                {this._strings.get("renameGroupLabel")}
                            </MenuItem>
                            <MenuItem
                                key="reorder-group"
                                icon={<Icon iconName="teams-drag-drop-offset" />}
                                onClick={this.onReorderTagsClicked}
                                disabled={viewState.disableReorderGroup}
                                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDReorderGroupMenuOption")}
                            >
                                {this._strings.get("reorderTags")}
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem
                                key="delete-group"
                                icon={<Icon iconName="teams-delete-svg" />}
                                onClick={this.onDeleteGroupClicked}
                                disabled={deleteMenuItemProps?.disabled}
                                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag")}
                                className="DeleteGroupMenuOption"
                            >
                                {this._strings.get("deleteTagShort")}
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            );
        }

        // Fallback to Fluent UI v8 IconButton when mute feature is disabled (original order without separator)
        const menuItems = [
            {
                key: "rename-group",
                className: classNames(styles.overflowMenuItem),
                iconProps: {
                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDRename")),
                    iconName: "teams-edit"
                },
                title: renameMenuItemProps?.disabled ? this._teamSettingsStrings.get("adminPermissionDisabledSettingLabel") : this._strings.get("renameGroupLabel"),
                ariaLabel: this._strings.get("renameGroupLabel"),
                text: this._strings.get("renameGroupLabel"),
                onClick: this.onRenameGroupClicked,
                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDRenameGroupMenuOption"),
                ...renameMenuItemProps
            },
            {
                key: "reorder-group",
                className: classNames(styles.overflowMenuItem),
                iconProps: {
                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDReOrder")),
                    iconName: "teams-drag-drop-offset"
                },
                ariaLabel: reorderGroupLabel,
                text: this._strings.get("reorderTags"),
                title: reorderGroupLabel,
                onClick: this.onReorderTagsClicked,
                disabled: viewState.disableReorderGroup,
                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDReorderGroupMenuOption")
            },
            {
                key: "delete-group",
                className: classNames(styles.overflowMenuItem, "DeleteGroupMenuOption"),
                iconProps: {
                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag")),
                    iconName: "teams-delete-svg"
                },
                ariaLabel: deleteGroupLabel,
                title: deleteMenuItemProps?.disabled ? this._teamSettingsStrings.get("adminPermissionDisabledSettingLabel") : deleteGroupLabel,
                text: this._strings.get("deleteTag"),
                onClick: this.onDeleteGroupClicked,
                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag"),
                ...deleteMenuItemProps
            }
        ];

        return <IconButton
                    role="menuitem"
                    title={this._strings.get("moreOptionsLabel")}
                    ariaLabel={this._strings.get("moreOptionsLabel")}
                    className={classNames(styles.settingIcon, styles.transparentButton, styles.moreIcon)}
                    styles={{ menuIcon: styles.buttonIcon }}
                    data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader")}
                    componentRef={this._moreMenuButton}
                    menuIconProps={{ iconName: "teams-more-svg" }}
                    menuProps={{
                        items: menuItems
                    }}
                />;
    }

    /**
     * Callback when key down is detected on the collapse tag button. Calls onTagCollapsed if the key is an action key
     */
    private onTagCollapseKeyDown = (key: React.KeyboardEvent<HTMLElement>) => {
        if (KeyboardUtils.isActionKeyPressed(key)) {
            key.preventDefault();
            this.onTagCollapsed();
        }
    }

    /**
     * Click handler when the arrow in tag name is clicked
     */
    private onTagCollapsed = () => {
        const viewState = this.props;
        const tagId = this.getTagId();
        if (tagId) {
            toggleTagView(schedulesViewStateStore(), tagId, !viewState.isTagCollapsed /* newCollapseState */);
        }
    }

    /**
     * Sets focus on the more menu icon button
     */
    private setFocusOnMoreMenuButton = () => {
        this._async.setTimeout(() => {
            if (this._moreMenuButton && this._moreMenuButton.current) {
                this._moreMenuButton.current.focus();
            }
        }, 0);
    }

    private setFocusOnTeamPicker = () => {
        this._async.setTimeout(() => {
            const teamPickerIconElement = document.getElementById(TEAM_PICKER_ICON_ID);
                if (teamPickerIconElement) {
                    teamPickerIconElement.focus();
                }
        }, 0);
    }
    /**
     * Click handler for rename group
     */
    private onRenameGroupClicked = () => {
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupClicked,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(this.props.scheduleCalendarType))]);

        // Cache existing group name
        const oldGroupName = this.getTagName();
        // Hide hover column functionality
        if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NewAddGroupExperience)) {
            this.hideHoverColumn(true);
        }
        showConfirmDialog(
           this._strings.get("renameGroupLabel"),
            "" /** No subtitle */,
            {
                bodyElement: (this.renderGroupNameTextField()),
                isBlocking: true,
                containerClassName: classNames(AutomationUtils.getAutomationId("confirm", "QAIDConfirmDialog"), styles.confirmDialog),
                okText: this._commonStrings.get("save")
            },
            () => {
                this.saveCurrentTagName();
                this.setFocusOnMoreMenuButton();
                InstrumentationService.logEvent(InstrumentationService.events.RenameGroupSaved,
                    [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                        InstrumentationUtils.getCurrentViewForInstrumentation(this.props.scheduleCalendarType))]);
                if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NewAddGroupExperience)) {
                    this.hideHoverColumn(false);
                }
            } /** onOK */,
            () => {
                // On clicking cancel, revert to old groupname
                setTagName(this._tagHeaderViewState, oldGroupName);
                this.saveCurrentTagName();
                this.setFocusOnMoreMenuButton();
                InstrumentationService.logEvent(InstrumentationService.events.RenameDialogCancelClicked,
                    [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                        InstrumentationUtils.getCurrentViewForInstrumentation(this.props.scheduleCalendarType))]);
                if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NewAddGroupExperience)) {
                    this.hideHoverColumn(false);
                }
            } /** onCancel */,
            false /** Ok button disabled */,
            true /** Show close button */,
            true /** Call cancel on dismiss */);
    }

    /**
     * Click handler for reorder tags button
     */
    private onReorderTagsClicked = () => {
         InstrumentationService.logEvent(InstrumentationService.events.ReorderTagsClicked, [
             getGenericEventPropertiesObject(
                 InstrumentationService.properties.CurrentView,
                 InstrumentationUtils.getCurrentViewForInstrumentation(this.props.scheduleCalendarType)
             )
         ]);
        setIsReorderTagsPanelOpen(schedulesViewStateStore(), true /* isReorderTagsPanelOpen */);
    }

    /**
     * Click handler for add member to tag
     */
    private onAddMemberToTagClicked = () => {
        if (this.props.tag) {
            setMemberDialogOpen(true);
            setMemberOpenDialogTriggerButton(this._addMember?.current ?? null);
            setMemberPanelValues(schedulesViewStateStore(), this.props.tag.id, null /* member */);
            InstrumentationService.logEvent(InstrumentationService.events.GroupAddMemberClicked);
        }
    }

    private setGlobalErrorMessage(groupName: string) {
        setGlobalMessages(appViewState().globalMessageViewState, [this._strings.get("groupNotDeleted").format(groupName)], MessageBarType.error, null /* action button title */, null /* action button callback */, false /* auto dismiss */, true /* is multiline */);
    }

    /**
     * Click handler for deleting a group
     */
    private onDeleteGroupClicked = () => {
        let viewState = this.props;
        if (viewState.tag) {
            InstrumentationService.logEvent(InstrumentationService.events.GroupDeleteOnScheduleClicked,
                [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(this.props.scheduleCalendarType))]);

            // If group has no name then show default name as Unnamed group
            let groupName = viewState.tag.name || this._commonStrings.get("unnamedGroup");

            showConfirmDialog(
                this._strings.get("deleteTag"),
                viewState.hasShifts
                    ? this._strings.get("deleteGroupMessageWithShifts").format(groupName)
                    : this._strings.get("deleteGroupMessage").format(groupName),
                {
                    okText: viewState.hasShifts ? "" : this._commonStrings.get("remove"),
                    isBlocking: true,
                    containerClassName: styles.confirmDialog
                },
                () => {
                    if (!viewState.hasShifts) {
                        try {
                            InstrumentationService.logEvent(InstrumentationService.events.GroupCommitDeleteOnScheduleClicked,
                                [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(this.props.scheduleCalendarType))]);
                            TagDataService.deleteTag(viewState.tag)
                            .then((tag: ITagEntity) => {
                                const addGroupElement = document.getElementById("addGroup");
                                if (addGroupElement) {
                                    addGroupElement.focus();
                                }
                                if (tag) {
                                    fireAccessibilityAlert(this._strings.get("groupDeleted").format(groupName));
                                } else {
                                    this.setGlobalErrorMessage(groupName);
                                }
                            });
                        } catch (error) {
                            this.setGlobalErrorMessage(groupName);
                        }
                        this.setFocusOnTeamPicker();
                    }
                },
                () => {
                    this.setFocusOnMoreMenuButton();
                },
                false /** Ok button disabled */,
                false /** Show close button */,
                true /** Call cancel on dismiss */);
       }
    }

    /**
     * Click handler for mute schedule group / unmute schedule group menu item.
     * Orchestrator handles confirmation dialog, optimistic UI update + API call + error reversion.
     */
    private onMuteScheduleGroupClicked = () => {
        const viewState = this.props;
        if (viewState.tag && TeamStore().teamId) {
            const teamId = TeamStore().teamId;
            const scheduleGroupId = viewState.tag.id;

            toggleMuteScheduleGroup(teamId, scheduleGroupId);
        }
    }

}